import { useState, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLlmCost } from '@/lib/query/hooks/useLlmCost';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PhaseStats, TaskLlmCost, TaskPhaseDetail } from '@/lib/types/mise/llmCost';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** コストを$表記でフォーマット */
function formatCost(value: number): string {
  return `$${value.toFixed(4)}`;
}

/** トークン数をカンマ区切りでフォーマット */
function formatTokens(value: number): string {
  return value.toLocaleString();
}

/** メトリクスカードのスタイル */
const metricCardSx = {
  p: 2,
  textAlign: 'center',
  transition: 'box-shadow 0.2s ease',
  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
};

const metricValueSx = {
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.02em',
};

/** PhaseStatsをサマリーカードに表示するコンポーネント */
function PhaseStatsCards({ stats }: { stats: PhaseStats }) {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper
          sx={{
            ...metricCardSx,
            background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.06))',
            borderColor: 'rgba(99,102,241,0.2)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            合計コスト
          </Typography>
          <Typography variant="h5" sx={{ ...metricValueSx, color: '#6366f1' }}>
            {formatCost(stats.total_cost)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            入力: {formatCost(stats.total_input_cost)} / 出力: {formatCost(stats.total_output_cost)}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            合計トークン数
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {formatTokens(stats.total_input_tokens + stats.total_output_tokens)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            入力: {formatTokens(stats.total_input_tokens)} / 出力:{' '}
            {formatTokens(stats.total_output_tokens)}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            タスク件数
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {stats.task_count.toLocaleString()}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            平均コスト/タスク
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {formatCost(stats.avg_cost_per_task)}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            店舗数合計
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {stats.total_seller_num.toLocaleString()}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            平均コスト/店舗
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {formatCost(stats.avg_cost_per_seller)}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            LLM実行回数
          </Typography>
          <Typography variant="h5" sx={metricValueSx}>
            {stats.total_llm_invoke_num.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            成功: {stats.total_llm_invoke_success.toLocaleString()} / 失敗:{' '}
            {stats.total_llm_invoke_fail.toLocaleString()} / リトライ:{' '}
            {stats.total_llm_invoke_retry.toLocaleString()}
          </Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Paper sx={metricCardSx}>
          <Typography variant="body2" color="text.secondary">
            LLM成功率
          </Typography>
          <Typography
            variant="h5"
            sx={{
              ...metricValueSx,
              color:
                stats.total_llm_invoke_num > 0 &&
                stats.total_llm_invoke_success / stats.total_llm_invoke_num >= 0.95
                  ? '#10b981'
                  : undefined,
            }}
          >
            {stats.total_llm_invoke_num > 0
              ? ((stats.total_llm_invoke_success / stats.total_llm_invoke_num) * 100).toFixed(1)
              : '0.0'}
            %
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

/** Rechartsカスタムツールチップ */
const chartTooltipStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  padding: '8px 12px',
};

const axisTickStyle = { fontSize: 11, fill: '#94a3b8' };

interface FlatTask {
  task: TaskLlmCost;
  phase: 'summary' | 'action' | '-';
  detail: TaskPhaseDetail | null;
}

export function LlmCostPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // デフォルト: 過去30日
  const defaultEnd = useMemo(() => formatDate(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  }, []);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedAccountCode, setSelectedAccountCode] = useState('');

  // アカウント一覧用（フィルタなし）
  const { data: allAccountData } = useLlmCost(startDate, endDate);

  const {
    data: llmCostData,
    isLoading: dataLoading,
    error: dataError,
  } = useLlmCost(startDate, endDate, selectedAccountCode || undefined);

  // アカウントコード一覧はフィルタなしのレスポンスから取得
  const accountCodes = useMemo(() => {
    if (!allAccountData?.account_costs) return [];
    return allAccountData.account_costs.map((a) => a.account_code).sort();
  }, [allAccountData?.account_costs]);

  // タスクリストをフェーズ別に展開
  const flatTasks = useMemo<FlatTask[]>(() => {
    if (!llmCostData?.tasks) return [];
    return llmCostData.tasks.flatMap((task) => {
      const rows: FlatTask[] = [];
      if (task.summary) rows.push({ task, phase: 'summary', detail: task.summary });
      if (task.action) rows.push({ task, phase: 'action', detail: task.action });
      if (!task.summary && !task.action) rows.push({ task, phase: '-', detail: null });
      return rows;
    });
  }, [llmCostData?.tasks]);

  if (authLoading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AppLayout>
        <Alert severity="warning">この機能を利用するにはログインが必要です。</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="LLMコスト">
      <Box sx={{ p: 3 }}>
        {/* フィルタバー */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight="bold">
              期間
            </Typography>
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Typography>〜</Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              select
              size="small"
              label="アカウント"
              value={selectedAccountCode}
              onChange={(e) => setSelectedAccountCode(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">すべてのアカウント</MenuItem>
              {accountCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {dataLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {dataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            LLMコストの取得に失敗しました。
          </Alert>
        )}

        {llmCostData && (
          <Grid container spacing={3}>
            {/* サマリーカード: タブ切り替え */}
            <Grid size={12}>
              <Tabs defaultValue="total">
                <TabsList>
                  <TabsTrigger value="total">合計</TabsTrigger>
                  <TabsTrigger value="summary">Summary (毎日)</TabsTrigger>
                  <TabsTrigger value="action">Action (週1)</TabsTrigger>
                </TabsList>
                <TabsContent value="total">
                  <PhaseStatsCards stats={llmCostData.summary.total} />
                </TabsContent>
                <TabsContent value="summary">
                  <PhaseStatsCards stats={llmCostData.summary.summary_phase} />
                </TabsContent>
                <TabsContent value="action">
                  <PhaseStatsCards stats={llmCostData.summary.action_phase} />
                </TabsContent>
              </Tabs>
            </Grid>

            {/* 日別コスト推移チャート（積み上げエリア） */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" mb={2}>
                  日別コスト推移
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={llmCostData.daily_costs}>
                    <defs>
                      <linearGradient id="gradSummary" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradAction" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={axisTickStyle}
                      tickFormatter={(v) => v.slice(5)}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => `$${v}`}
                      tick={axisTickStyle}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number, name: string) => [`$${value.toFixed(4)}`, name]}
                      labelFormatter={(label) => `日付: ${label}`}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                    <Area
                      type="monotone"
                      dataKey="summary_cost"
                      stackId="cost"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fill="url(#gradSummary)"
                      name="Summary (毎日)"
                    />
                    <Area
                      type="monotone"
                      dataKey="action_cost"
                      stackId="cost"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      fill="url(#gradAction)"
                      name="Action (週1)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* アカウント別コストチャート */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" mb={2}>
                  アカウント別コスト
                </Typography>
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(300, llmCostData.account_costs.length * 40)}
                >
                  <BarChart data={llmCostData.account_costs} layout="vertical">
                    <defs>
                      <linearGradient id="gradBar" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#818cf8" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `$${v}`}
                      tick={axisTickStyle}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="account_code"
                      width={150}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number) => [`$${value.toFixed(4)}`, 'コスト']}
                      cursor={{ fill: 'rgba(99, 102, 241, 0.04)' }}
                    />
                    <Bar
                      dataKey="total_cost"
                      fill="url(#gradBar)"
                      name="合計コスト"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* 詳細テーブル */}
            <Grid size={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" mb={1}>
                  タスク詳細
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>タスクID</TableCell>
                        <TableCell>アカウント</TableCell>
                        <TableCell>基準日</TableCell>
                        <TableCell>フェーズ</TableCell>
                        <TableCell>モデル</TableCell>
                        <TableCell align="right">入力トークン</TableCell>
                        <TableCell align="right">出力トークン</TableCell>
                        <TableCell align="right">入力コスト</TableCell>
                        <TableCell align="right">出力コスト</TableCell>
                        <TableCell align="right">合計コスト</TableCell>
                        <TableCell align="right">店舗数</TableCell>
                        <TableCell align="right">店舗平均コスト</TableCell>
                        <TableCell align="right">LLM実行回数</TableCell>
                        <TableCell align="right">成功/失敗/リトライ</TableCell>
                        <TableCell>ステータス</TableCell>
                        <TableCell>開始日時</TableCell>
                        <TableCell>終了日時</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flatTasks.map((row, idx) => (
                        <TableRow
                          key={`${row.task.id}-${row.phase}-${idx}`}
                          sx={{
                            backgroundColor:
                              row.phase === 'summary'
                                ? 'rgba(99, 102, 241, 0.04)'
                                : row.phase === 'action'
                                  ? 'rgba(245, 158, 11, 0.04)'
                                  : undefined,
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.task.id}</TableCell>
                          <TableCell>{row.task.account_code}</TableCell>
                          <TableCell>{row.task.base_date}</TableCell>
                          <TableCell>
                            {row.phase === 'summary'
                              ? 'Summary'
                              : row.phase === 'action'
                                ? 'Action'
                                : '-'}
                          </TableCell>
                          <TableCell>{row.detail?.model ?? '-'}</TableCell>
                          <TableCell align="right">
                            {row.detail ? formatTokens(row.detail.input_tokens) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail ? formatTokens(row.detail.output_tokens) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail ? formatCost(row.detail.input_cost) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail ? formatCost(row.detail.output_cost) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail ? formatCost(row.detail.total_cost) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail?.seller_num != null
                              ? row.detail.seller_num.toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail?.cost_per_seller != null
                              ? formatCost(row.detail.cost_per_seller)
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail?.llm_invoke_num != null
                              ? row.detail.llm_invoke_num.toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.detail?.llm_invoke_success != null
                              ? `${row.detail.llm_invoke_success}/${row.detail.llm_invoke_fail}/${row.detail.llm_invoke_retry}`
                              : '-'}
                          </TableCell>
                          <TableCell>{row.task.batch_llm_status}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.task.process_started_at ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {row.task.process_finished_at ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {flatTasks.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={17} align="center">
                            データがありません
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </AppLayout>
  );
}
